import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Building, Bell, Moon, Sun, Monitor,
  Save, Key, Trash2, Eye, EyeOff, Lock,
  CheckCircle2, AlertTriangle, Palette,
  Mail, BookOpen, Zap, Link2,
  FileDown, Target, Sparkles, Edit3,
  Search, Shield, Smartphone, HardDrive,
  Layout, Clock, LogOut, Copy,
  Gift, DollarSign, CreditCard,
  Download, ChevronRight, Share2,
 MessageCircle,
  Users, Globe
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import Sessions from './Sessions';
import ServiceContextInput from '../../components/ServiceContextInput';

// ─── Toggle Switch ───
const ToggleSwitch = ({ checked, onChange }) => (
  <button onClick={() => onChange(!checked)} style={{
    width: 48, height: 28, borderRadius: 14, border: 'none',
    background: checked ? 'var(--success)' : 'var(--bg-tertiary)',
    cursor: 'pointer', position: 'relative', flexShrink: 0,
    transition: 'background 0.2s ease',
  }}>
    <div style={{
      position: 'absolute', top: 3, left: checked ? 23 : 3,
      width: 22, height: 22, borderRadius: '50%', background: 'white',
      transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

// ─── Section Card ───
const Section = ({ icon, title, children, action }) => (
  <div className="billing-plan-card" style={{ marginBottom: 'var(--space-xl)' }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 'var(--space-lg)', paddingBottom: 'var(--space-md)',
      borderBottom: '1px solid var(--border-primary)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
        <span style={{ color: 'var(--accent-primary)' }}>{icon}</span>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 'clamp(13px, 2.5vw, 14px)', fontWeight: 600, margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </h3>
      </div>
      {action}
    </div>
    {children}
  </div>
);

// ─── Input Field ───
const InputField = ({ icon, label, value, onChange, placeholder, type = 'text', disabled }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ marginBottom: 'var(--space-md)' }}>
      <label style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-md)', background: disabled ? 'var(--bg-tertiary)' : 'var(--input-bg)', overflow: 'hidden', opacity: disabled ? 0.6 : 1 }}>
        <span style={{ padding: '0 var(--space-md)', color: 'var(--text-tertiary)', flexShrink: 0 }}>{icon}</span>
        <input
          type={type === 'password' ? (show ? 'text' : 'password') : type}
          value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder} disabled={disabled}
          style={{ flex: 1, padding: 'var(--space-md) var(--space-md) var(--space-md) 0', border: 'none', background: 'none', color: 'var(--text-primary)', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
        />
        {type === 'password' && (
          <button type="button" onClick={() => setShow(!show)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 'var(--space-md)' }}>
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Save Button ───
const SaveBtn = ({ loading, onClick, label = 'Save Changes' }) => (
  <button onClick={onClick} disabled={loading} style={{
    padding: '10px 20px', borderRadius: 'var(--radius-md)', border: 'none',
    background: 'var(--accent-gradient)', color: 'white', cursor: 'pointer',
    fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8,
    opacity: loading ? 0.7 : 1,
  }}>
    {loading ? <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />}
    {loading ? 'Saving...' : label}
  </button>
);

// ─── Coming Soon Badge ───
const ComingSoon = () => (
  <span style={{
    padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
    background: 'var(--warning-bg)', color: 'var(--warning)',
    marginLeft: 8, letterSpacing: '0.03em',
  }}>COMING SOON</span>
);

// ─── KPI Mini Card ───
const MiniStat = ({ icon, label, value, color }) => (
  <div style={{ textAlign: 'center', padding: 'var(--space-md)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)' }}>
    <div style={{ color, marginBottom: 4 }}>{icon}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
    <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function Settings() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  // Profile
  const [form, setForm] = useState({ full_name: '', company_name: '', service_name: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Preferences
  const [prefs, setPrefs] = useState({ default_enrich: true, default_score: true, default_landing: 'dashboard', results_per_page: 20, auto_export: false, export_format: 'csv', compact_mode: false });
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Scoring
  const [scoringPrefs, setScoringPrefs] = useState({ hot_threshold: 70, min_reviews: 0 });
  const [savingScoring, setSavingScoring] = useState(false);

  // Notifications
  const [notifs, setNotifs] = useState({
    search_complete: true, weekly_summary: true, product_updates: true, tips_tutorials: true,
    hot_lead_alert: false,
    search_complete_email: true, search_complete_push: false,
    hot_lead_email: true, hot_lead_push: true,
    weekly_summary_email: true,
  });
  const [savingNotifs, setSavingNotifs] = useState(false);

  // Plan & Billing
  const [currentPlan, setCurrentPlan] = useState('free');
  const [role, setRole] = useState('user');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('sdr');
  const [teamLoading, setTeamLoading] = useState(false);
  const [planConfig, setPlanConfig] = useState({});
  const [leadsPerSearch, setLeadsPerSearch] = useState(5);
  const [billingSummary, setBillingSummary] = useState(null);

  // Security
  const [twoFA, setTwoFA] = useState(false);
  const [loadingTwoFA, setLoadingTwoFA] = useState(true);
  const [cpw, setCpw] = useState({ current: '', new: '', confirm: '' });
  const [cpwLoading, setCpwLoading] = useState(false);
  const [cpwSuccess, setCpwSuccess] = useState(false);
  const [cpwError, setCpwError] = useState('');

  // Activity Log
  const [activityLog, setActivityLog] = useState([]);

  // Referral
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState({ clicks: 0, signups: 0, earnings: 0 });
  const [copiedRef, setCopiedRef] = useState(false);

  // Delete Account
  const [showDelete, setShowDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [userTimezone, setUserTimezone] = useState('auto');
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [showClearHistory, setShowClearHistory] = useState(false);
  const [clearHistoryText, setClearHistoryText] = useState('');
  const [clearingHistory, setClearingHistory] = useState(false);

  const referralLink = referralCode ? `https://ileadx.io/?ref=${referralCode}` : '';

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [meRes, prefsRes, notifsRes, twoFaRes, plansRes, billingRes] = await Promise.all([
        api.get('/api/auth/me'),
        api.get('/api/auth/preferences'),
        api.get('/api/auth/notifications'),
        api.get('/api/auth/2fa/status'),
        api.get('/api/billing/plans/full'),
        api.get('/api/billing/plans'),
      ]);

      const d = meRes.data;
      setRole(d.role || 'user');
      setForm({ full_name: d.full_name || '', company_name: d.company_name || '', service_name: d.service_name || '' });
      setCurrentPlan(d.plan || 'free');
      setLeadsPerSearch(d.leads_per_search || 5);
      setReferralCode(d.referral_code || '');
      setUserTimezone(d.timezone || 'auto');

      if (prefsRes.data) {
        setPrefs(prev => ({ ...prev, ...prefsRes.data }));
        // Also set scoring prefs from API
        if (prefsRes.data.hot_threshold) {
          setScoringPrefs(prev => ({ ...prev, hot_threshold: prefsRes.data.hot_threshold }));
        }
        if (prefsRes.data.min_reviews !== undefined) {
          setScoringPrefs(prev => ({ ...prev, min_reviews: prefsRes.data.min_reviews }));
        }
      }
      if (notifsRes.data) setNotifs(prev => ({ ...prev, ...notifsRes.data }));
      if (plansRes.data?.plans) setPlanConfig(plansRes.data.plans);
      setTwoFA(twoFaRes.data.enabled);

      // Billing summary
      if (billingRes.data) {
        const pd = billingRes.data.plan_details || {};
        setBillingSummary({
          plan: billingRes.data.current_plan,
          status: billingRes.data.subscription_status,
          periodEnd: billingRes.data.period_end,
          planName: pd.name || billingRes.data.current_plan,
        });
      }

      // Load activity log
      try {
        const activityRes = await api.get('/api/auth/activity', { params: { limit: 5 } });
        setActivityLog(activityRes.data.events || []);
      } catch (e) {
        console.log('Activity log not available:', e);
      }

      // Load referral stats if code exists
      if (d.referral_code) {
        try {
          const refRes = await api.get('/api/referral/stats');
          setReferralStats(refRes.data);
        } catch {}
      }
    } catch {} finally { setLoadingTwoFA(false); }
  };

  // ── Handlers ──
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/api/auth/profile', { full_name: form.full_name, company_name: form.company_name, service_name: form.service_name });
      toast.success('Profile updated!');
    } catch (err) {
      if (err.response?.status === 429) toast.error('Service context change limit reached.');
      else toast.error('Failed to update.');
    } finally { setSavingProfile(false); }
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try { await api.put('/api/auth/preferences', prefs); toast.success('Preferences saved!'); }
    catch { toast.error('Failed.'); }
    finally { setSavingPrefs(false); }
  };

  const handleSaveScoring = async () => {
    setSavingScoring(true);
    try {
      await api.put('/api/auth/preferences', { 
        hot_threshold: scoringPrefs.hot_threshold,
        min_reviews: scoringPrefs.min_reviews 
      });
      toast.success('Scoring preferences saved!');
    } catch {
      // Fallback to localStorage
      localStorage.setItem('ileadx_scoring_prefs', JSON.stringify(scoringPrefs));
      toast.success('Saved locally');
    }
    setSavingScoring(false);
  };

  const handleSaveNotifs = async () => {
    setSavingNotifs(true);
    try { await api.put('/api/auth/notifications', notifs); toast.success('Notification preferences saved!'); }
    catch { toast.error('Failed.'); }
    finally { setSavingNotifs(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault(); setCpwError('');
    if (!cpw.current) { setCpwError('Enter current password'); return; }
    if (cpw.new.length < 8) { setCpwError('Min 8 characters'); return; }
    if (cpw.new !== cpw.confirm) { setCpwError('Passwords do not match'); return; }
    setCpwLoading(true);
    try {
      await api.post('/api/auth/change-password', { current_password: cpw.current, new_password: cpw.new });
      setCpwSuccess(true); setCpw({ current: '', new: '', confirm: '' });
      toast.success('Password changed!');
      setTimeout(() => setCpwSuccess(false), 5000);
    } catch (err) { setCpwError(err.response?.data?.detail || 'Failed'); }
    finally { setCpwLoading(false); }
  };

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(referralLink);
    setCopiedRef(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopiedRef(false), 2000);
  };

  const handleSaveTimezone = async () => {
    setSavingTimezone(true);
    try {
      await api.put('/api/auth/timezone', { timezone: userTimezone });
      toast.success('Timezone updated!');
    } catch { toast.error('Failed to update timezone'); }
    finally { setSavingTimezone(false); }
  };

  const handleClearHistory = async () => {
    setClearingHistory(true);
    try {
      const res = await api.delete('/api/auth/search-history');
      toast.success(res.data.message || 'Search history cleared');
      setShowClearHistory(false);
      setClearHistoryText('');
    } catch { toast.error('Failed to clear history'); }
    finally { setClearingHistory(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await api.delete('/api/auth/account'); } catch {}
    localStorage.clear(); window.location.href = '/register';
  };

  const getStrength = (pw) => {
    if (!pw) return { score: 0, label: '', color: '' };
    let s = 0;
    if (pw.length >= 8) s++; if (pw.length >= 12) s++; if (/[A-Z]/.test(pw)) s++; if (/[0-9]/.test(pw)) s++; if (/[^A-Za-z0-9]/.test(pw)) s++;
    const levels = [{ label: 'Weak', color: 'var(--error)' },{ label: 'Fair', color: 'var(--warning)' },{ label: 'Good', color: 'var(--warning)' },{ label: 'Strong', color: 'var(--success)' },{ label: 'Very Strong', color: 'var(--success)' }];
    return { score: Math.min(s, 5), ...levels[Math.min(s, 4)] };
  };
  const strength = getStrength(cpw.new);

  const formatTime = (d) => {
    if (!d) return '';
    const date = new Date(d); const now = new Date(); const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isProOrAbove = ['pro', 'enterprise'].includes(currentPlan);

  return (
    <div className="page-content" style={{ maxWidth: 'clamp(320px, 100%, 1000px)', margin: '0 auto' }}>
      
      {/* Header */}
      <div className="billing-header">
        <h1><span style={{ fontSize: 28 }}>⚙️</span> Settings</h1>
        <p>Configure your account, preferences, and security.</p>
      </div>

      {/* ═══════════ PROFILE ═══════════ */}
      <Section icon={<User size={16} />} title="Profile Information">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
          <InputField icon={<User size={14} />} label="Full Name" value={form.full_name} onChange={v => setForm({...form, full_name: v})} placeholder="Your full name" />
          <InputField icon={<Building size={14} />} label="Company Name" value={form.company_name} onChange={v => setForm({...form, company_name: v})} placeholder="Your company" />
        </div>
        <InputField icon={<Mail size={14} />} label="Email Address" value={user?.email || ''} disabled placeholder="your@email.com" />
        <div style={{ marginBottom: 'var(--space-md)' }}>
          <label style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' }}>Service Context</label>
          <ServiceContextInput value={form.service_name} onChange={v => setForm({...form, service_name: v})} disabled={false} />
        </div>
        <SaveBtn loading={savingProfile} onClick={handleSaveProfile} />
      </Section>

      {/* ═══════════ APPEARANCE ═══════════ */}
      <Section icon={<Palette size={16} />} title="Appearance">
        <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { mode: 'dark', icon: <Moon size={18} />, label: 'Dark', desc: 'Easy on the eyes' },
            { mode: 'light', icon: <Sun size={18} />, label: 'Light', desc: 'Clean and bright' },
            { mode: 'system', icon: <Monitor size={18} />, label: 'System', desc: 'Matches your OS' },
          ].map(({ mode, icon, label, desc }) => (
            <button key={mode} onClick={() => setTheme(mode)} style={{
              flex: 1, minWidth: 100, padding: 'var(--space-md)', borderRadius: 'var(--radius-lg)',
              border: theme === mode ? '2px solid var(--accent-primary)' : '1px solid var(--border-primary)',
              background: theme === mode ? 'var(--accent-glow)' : 'var(--bg-tertiary)',
              color: theme === mode ? 'var(--accent-primary)' : 'var(--text-secondary)',
              cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s ease',
            }}>
              <div style={{ marginBottom: 4 }}>{icon}</div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
              <div style={{ fontSize: 11, marginTop: 1, opacity: 0.7 }}>{desc}</div>
            </button>
          ))}
        </div>
      </Section>

      {/* ═══════════ SECURITY ═══════════ */}
      <Section icon={<Shield size={16} />} title="Security">
        {/* Password */}
        <h4 style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Change Password</h4>
        {cpwSuccess ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: 'var(--success-bg)', borderRadius: 8, border: '1px solid var(--success)', marginBottom: 16 }}>
            <CheckCircle2 size={16} color="var(--success)" />
            <span style={{ color: 'var(--success)', fontWeight: 600, fontSize: 13 }}>Password changed successfully!</span>
          </div>
        ) : (
          <form onSubmit={handleChangePassword} style={{ marginBottom: 16 }}>
            <InputField icon={<Lock size={14} />} label="Current Password" value={cpw.current} onChange={v => setCpw({...cpw, current: v})} placeholder="Enter current password" type="password" />
            <InputField icon={<Key size={14} />} label="New Password" value={cpw.new} onChange={v => setCpw({...cpw, new: v})} placeholder="Min 8 characters" type="password" />
            {cpw.new && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
                  {[1,2,3,4,5].map(l => <div key={l} style={{ flex: 1, height: 3, borderRadius: 2, background: l <= strength.score ? strength.color : 'var(--border-primary)' }} />)}
                </div>
                <span style={{ color: strength.color, fontSize: 11, fontWeight: 500 }}>{strength.label}</span>
              </div>
            )}
            <InputField icon={<Shield size={14} />} label="Confirm New Password" value={cpw.confirm} onChange={v => setCpw({...cpw, confirm: v})} placeholder="Re-enter new password" type="password" />
            {cpwError && <div style={{ padding: '8px 12px', background: 'var(--error-bg)', borderRadius: 8, border: '1px solid var(--error)', color: 'var(--error)', fontSize: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={12} />{cpwError}</div>}
            <SaveBtn loading={cpwLoading} onClick={handleChangePassword} label="Change Password" />
          </form>
        )}

        <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: 14, marginTop: 8 }}>
          {/* 2FA */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Smartphone size={16} color="var(--text-secondary)" />
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>Two-Factor Authentication</div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                  {twoFA ? '✅ Enabled,  Your account is protected' : 'Add an extra layer of security'}
                </div>
              </div>
            </div>
            {twoFA ? (
              <button onClick={() => navigate('/dashboard/security')} style={{
                padding: '6px 14px', borderRadius: 8, border: '1px solid var(--accent-primary)',
                background: 'transparent', color: 'var(--accent-primary)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              }}>Manage</button>
            ) : currentPlan === 'free' ? (
              <button onClick={() => navigate('/dashboard/billing?tab=plans')} style={{
                padding: '6px 14px', borderRadius: 8, border: '1px solid var(--warning)',
                background: 'transparent', color: 'var(--warning)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 4,
              }}><Lock size={12} /> Upgrade to Enable</button>
            ) : (
              <button onClick={() => navigate('/dashboard/security')} style={{
                padding: '6px 14px', borderRadius: 8, border: '1px solid var(--accent-primary)',
                background: 'transparent', color: 'var(--accent-primary)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              }}>Enable</button>
            )}
          </div>
        </div>

        {/* Activity Log */}
        <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: 14, marginTop: 8 }}>
          <h4 style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Recent Activity</h4>
          {activityLog.length > 0 ? (
            activityLog.map((event, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                <span>{event.icon || '📋'}</span>
                <span style={{ flex: 1 }}>{event.description}</span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{formatTime(event.timestamp || event.created_at)}</span>
              </div>
            ))
          ) : (
            <p style={{ color: 'var(--text-tertiary)', fontSize: 12, padding: '8px 0' }}>No recent activity to show.</p>
          )}
        </div>

        {/* Sessions inline */}
        <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: 14, marginTop: 8 }}>
          <h4 style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Active Sessions</h4>
          <Sessions />
        </div>
      </Section>

      {/* ═══════════ NOTIFICATIONS ═══════════ */}
      <Section icon={<Bell size={16} />} title="Notifications">
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 14 }}>Choose which notifications you receive and how.</p>
        
        {[
          { key: 'search_complete', icon: <Search size={14} />, label: 'Search Complete', desc: 'When a background search finishes' },
          { key: 'weekly_summary', icon: <Mail size={14} />, label: 'Weekly Summary', desc: 'Monday morning lead generation digest' },

        ].map(item => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-primary)' }}>
            <span style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>{item.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>{item.label}</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 2 }}>{item.desc}</div>
            </div>
            <ToggleSwitch checked={notifs[item.key]} onChange={v => setNotifs({...notifs, [item.key]: v})} />
          </div>
        ))}
        <div style={{ marginTop: 14 }}><SaveBtn loading={savingNotifs} onClick={handleSaveNotifs} /></div>
      </Section>

      {/* ═══════════ SEARCH & SCORING ═══════════ */}
      <Section icon={<Target size={16} />} title="Search & Scoring">
        <div style={{ position: 'relative' }}>
          <div style={{
            opacity: (currentPlan === 'free' || currentPlan === 'starter') ? 0.4 : 1,
            pointerEvents: (currentPlan === 'free' || currentPlan === 'starter') ? 'none' : 'auto',
          }}>
            {(role === 'admin' || role === 'super_admin') && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-primary)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}><Link2 size={14} /></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>Auto-enrich leads</div>
                    <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Scrape websites for emails, phones, and social links</div>
                  </div>
                  <ToggleSwitch checked={prefs.default_enrich} onChange={v => setPrefs({...prefs, default_enrich: v})} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-primary)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}><Sparkles size={14} /></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>Auto-score leads</div>
                    <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Use AI to generate scores, pain points, and pitch angles</div>
                  </div>
                  <ToggleSwitch checked={prefs.default_score} onChange={v => setPrefs({...prefs, default_score: v})} />
                </div>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <label style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' }}>Hot Lead Threshold: {scoringPrefs.hot_threshold}+</label>
              <input type="range" min="50" max="95" step="5" value={scoringPrefs.hot_threshold} onChange={e => setScoringPrefs({...scoringPrefs, hot_threshold: parseInt(e.target.value)})} style={{ width: '100%', accentColor: 'var(--accent-primary)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-tertiary)' }}>
                <span>50 (More)</span><span>95 (Best only)</span>
              </div>
            </div>

            {(role === 'admin' || role === 'super_admin') && (
              <div style={{ marginTop: 14 }}>
                <label style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' }}>Export Format</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['csv', 'excel', 'gsheet'].map(fmt => (
                    <button key={fmt} onClick={() => setPrefs({...prefs, export_format: fmt})} style={{
                      padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: prefs.export_format === fmt ? '2px solid var(--accent-primary)' : '1px solid var(--border-primary)',
                      background: prefs.export_format === fmt ? 'var(--accent-glow)' : 'transparent',
                      color: prefs.export_format === fmt ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    }}>{fmt === 'csv' ? '📄 CSV' : fmt === 'excel' ? '📊 Excel' : '📋 Sheets'}</button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <SaveBtn loading={savingScoring} onClick={handleSaveScoring} label="Save Scoring Preferences" />
            </div>
          </div>

          {(currentPlan === 'free' || currentPlan === 'starter') && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 8, borderRadius: 'var(--radius-md)',
            }}>
              <Lock size={16} color="var(--text-secondary)" />
              <a href="/dashboard/billing?tab=plans" style={{ color: 'var(--accent-primary)', fontSize: 12, fontWeight: 600, textDecoration: 'none', background: 'transparent', border: '1px solid var(--border-primary)', borderRadius: 6, padding: '4px 12px' }}>
                Upgrade to Growth
              </a>
            </div>
          )}
        </div>
      </Section>

      {/* ═══════════ REFERRALS ═══════════ */}
      <Section icon={<Share2 size={16} />} title="Referrals">
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 14 }}>Share iLeadX and earn rewards when people sign up.</p>
        
        {referralLink ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <input value={referralLink} readOnly style={{
                flex: 1, padding: '10px 14px', borderRadius: 8,
                border: '1px solid var(--accent-primary)', background: 'var(--accent-glow)',
                color: 'var(--accent-primary)', fontSize: 13, outline: 'none', fontFamily: 'monospace',
              }} />
              <button onClick={handleCopyReferral} style={{
                padding: '10px 16px', borderRadius: 8, border: 'none',
                background: copiedRef ? 'var(--success)' : 'var(--accent-gradient)',
                color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
              }}>
                {copiedRef ? <><CheckCircle2 size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>

            {/* Share Buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <ShareBtn icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>} label="Tweet" color="#1DA1F2"
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('I\'m finding qualified B2B leads in under 2 minutes with @iLeadX. Try it free: ' + referralLink)}`} />
              <ShareBtn icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>} label="Share" color="#1877F2"
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`} />
              <ShareBtn icon={<MessageCircle size={14} />} label="WhatsApp" color="#25D366"
                href={`https://wa.me/?text=${encodeURIComponent('Try iLeadX for free,  AI-powered B2B lead generation: ' + referralLink)}`} />
              <ShareBtn icon={<Mail size={14} />} label="Email" color="#666"
                href={`mailto:?subject=Try iLeadX,  AI-Powered B2B Leads&body=Hey! I've been using iLeadX to find qualified B2B leads. You get 3 free searches when you sign up: ${referralLink}`} />
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <MiniStat icon={<Users size={16} />} label="Clicks" value={referralStats.clicks || 0} color="var(--accent-primary)" />
              <MiniStat icon={<User size={16} />} label="Signups" value={referralStats.signups || 0} color="var(--success)" />
              <MiniStat icon={<DollarSign size={16} />} label="Earned" value={`$${referralStats.earnings || 0}`} color="var(--warning)" />
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)', fontSize: 13 }}>
            <p>Referral system is coming soon!</p>
            <ComingSoon />
          </div>
        )}
      </Section>

      {/* ═══════════ BILLING SUMMARY ═══════════ */}
      <Section icon={<CreditCard size={16} />} title="Billing Summary"
        action={<button onClick={() => navigate('/dashboard/billing')} style={{
          padding: '6px 14px', borderRadius: 8, border: '1px solid var(--accent-primary)',
          background: 'transparent', color: 'var(--accent-primary)',
          cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>Manage <ChevronRight size={14} /></button>}
      >
        {billingSummary ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginBottom: 2 }}>Current Plan</div>
              <div style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, textTransform: 'capitalize' }}>{billingSummary.plan}</div>
            </div>
            <div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginBottom: 2 }}>Status</div>
              <span style={{
                padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                background: billingSummary.status === 'active' ? 'var(--success-bg)' : billingSummary.status === 'cancelled' ? 'var(--error-bg)' : 'var(--warning-bg)',
                color: billingSummary.status === 'active' ? 'var(--success)' : billingSummary.status === 'cancelled' ? 'var(--error)' : 'var(--warning)',
                textTransform: 'capitalize',
              }}>{billingSummary.status || 'active'}</span>
            </div>
            {billingSummary.periodEnd && (
              <div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginBottom: 2 }}>Renews</div>
                <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>
                  {new Date(billingSummary.periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
            )}
            <div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginBottom: 2 }}>Leads per Search</div>
              <div style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700 }}>{leadsPerSearch}</div>
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>Loading billing info...</p>
        )}
      </Section>

      {/* ═══════════ INTEGRATIONS ═══════════ */}
      <Section icon={<Link2 size={16} />} title="Integrations"
        action={<button onClick={() => navigate('/dashboard/integrations')} style={{
          padding: '6px 14px', borderRadius: 8, border: '1px solid var(--accent-primary)',
          background: 'transparent', color: 'var(--accent-primary)',
          cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>Configure <ChevronRight size={14} /></button>}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
          <IntegrationCard icon="https://cdn.worldvectorlogo.com/logos/zapier-1.svg" name="Zapier" desc="5,000+ apps" />
          <IntegrationCard icon="https://cdn.worldvectorlogo.com/logos/google-sheets.svg" name="Google Sheets" desc="Export leads" />
          <IntegrationCard icon="https://cdn.worldvectorlogo.com/logos/hubspot.svg" name="HubSpot" desc="CRM sync" />
          <IntegrationCard icon="https://cdn.worldvectorlogo.com/logos/slack.svg" name="Slack" desc="Alerts" />
          <IntegrationCard icon="https://cdn.worldvectorlogo.com/logos/mailchimp.svg" name="Mailchimp" desc="Email campaigns" />
          <IntegrationCard icon="https://cdn.worldvectorlogo.com/logos/notion.svg" name="Notion" desc="Databases" />
        </div>
        <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--warning-bg)', borderRadius: 8, border: '1px solid var(--warning)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <Zap size={14} color="var(--warning)" />
          <span style={{ color: 'var(--warning)', fontWeight: 500 }}>API Access & Webhooks</span>
          <ComingSoon />
        </div>
      </Section>

      {/* ═══════════ TEAM (Pro/Enterprise) ═══════════ */}
      <Section icon={<Users size={16} />} title="Team">
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Users size={32} color="var(--text-tertiary)" style={{ marginBottom: 8 }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>
            {isProOrAbove ? 'Team management is coming soon.' : 'Team management is available on Pro and Enterprise plans.'}
          </p>
          <ComingSoon />
        </div>
      </Section>

      {/* ═══════════ LANGUAGE & REGION ═══════════ */}
      <Section icon={<Globe size={16} />} title="Language & Region">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <label style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' }}>Language</label>
            <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              English (US) <ComingSoon />
            </div>
          </div>
          <div>
            <label style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' }}>Time Zone</label>
            <select value={userTimezone} onChange={e => setUserTimezone(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
              <option value="auto">Auto-detect (Browser)</option>
              <option value="UTC">UTC (Coordinated Universal Time)</option>
              <option value="Africa/Lagos">West Africa Time (GMT+1)</option>
              <option value="Africa/Nairobi">East Africa Time (GMT+3)</option>
              <option value="Africa/Johannesburg">South Africa (GMT+2)</option>
              <option value="Europe/London">London (GMT+0)</option>
              <option value="Europe/Paris">Paris / Berlin (GMT+1)</option>
              <option value="America/New_York">Eastern Time (GMT-5)</option>
              <option value="America/Chicago">Central Time (GMT-6)</option>
              <option value="America/Denver">Mountain Time (GMT-7)</option>
              <option value="America/Los_Angeles">Pacific Time (GMT-8)</option>
              <option value="Asia/Dubai">Dubai (GMT+4)</option>
              <option value="Asia/Kolkata">India (GMT+5:30)</option>
              <option value="Asia/Singapore">Singapore (GMT+8)</option>
              <option value="Asia/Tokyo">Tokyo (GMT+9)</option>
              <option value="Australia/Sydney">Sydney (GMT+10)</option>
            </select>
            <button onClick={handleSaveTimezone} disabled={savingTimezone} style={{
              marginTop: 8, padding: '6px 14px', borderRadius: 6, border: '1px solid var(--accent-primary)',
              background: 'transparent', color: 'var(--accent-primary)',
              cursor: 'pointer', fontSize: 11, fontWeight: 600,
            }}>{savingTimezone ? 'Saving...' : 'Save Timezone'}</button>
          </div>
        </div>
      </Section>

      {/* ═══════════ PRIVACY & DATA ═══════════ */}
      <Section icon={<HardDrive size={16} />} title="Privacy & Data">
        
        {/* Data Export */}
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Export Your Data</h4>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 10 }}>
            Download all your leads, searches, and account data in JSON format. Your data belongs to you.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={async () => {
              try {
                const res = await api.get('/api/auth/export-data?format=json', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `ileadx_data_${new Date().toISOString().slice(0,10)}.json`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('Data exported as JSON');
              } catch { toast.error('Export failed'); }
            }} style={{
              padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border-primary)',
              background: 'transparent', color: 'var(--text-secondary)',
              cursor: 'pointer', fontSize: 12, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Download size={14} /> Leads & Searches (JSON)
            </button>
            <button onClick={async () => {
              try {
                const res = await api.get('/api/auth/export-data?format=csv', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `ileadx_leads_${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('Data exported as CSV');
              } catch { toast.error('Export failed'); }
            }} style={{
              padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border-primary)',
              background: 'transparent', color: 'var(--text-secondary)',
              cursor: 'pointer', fontSize: 12, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <FileDown size={14} /> Complete CSV Export
            </button>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: 14 }}>

          {/* Data Retention */}
          <h4 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Data Retention</h4>
          {currentPlan === 'enterprise' && (
            <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--success-bg)', borderRadius: 8, border: '1px solid var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 500 }}>📝 Data Processing Agreement — Active</span>
              <a href="/dashboard/dpa" style={{ color: 'var(--success)', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>View DPA →</a>
            </div>
          )}
          <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 10 }}>
            We retain your lead data and search history according to your plan. Free plans: 30 days. Paid plans: 90 days to unlimited.
          </p>
          <div style={{ padding: '10px 14px', background: 'var(--info-bg)', borderRadius: 8, border: '1px solid var(--info)', marginBottom: 14, fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--info)' }}>
              <Clock size={14} />
              <span>Your plan: <strong>{currentPlan === 'free' ? '30 days retention' : currentPlan === 'starter' ? '90 days retention' : 'Unlimited retention'}</strong></span>
            </div>
          </div>

          {/* Cookie Preferences — admin only */}
          {(role === 'admin' || role === 'super_admin') && (
          <div><h4 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Cookie Preferences</h4>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 10 }}>
            We use essential cookies for authentication and optional cookies for analytics. You can manage your preferences below.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border-primary)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>Essential Cookies</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Required for login and security. Always enabled.</div>
            </div>
            <ToggleSwitch checked={true} onChange={() => {}} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border-primary)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>Analytics Cookies</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Help us improve iLeadX by understanding how you use it.</div>
            </div>
            <ToggleSwitch checked={prefs.analytics_cookies !== false} onChange={v => setPrefs({...prefs, analytics_cookies: v})} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>Marketing Cookies</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Used for referral tracking and promotional offers.</div>
            </div>
            <ToggleSwitch checked={prefs.marketing_cookies !== false} onChange={v => setPrefs({...prefs, marketing_cookies: v})} />
          </div>

          {/* Email Consent */}
          <h4 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Email Preferences</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border-primary)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>Transactional Emails</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Payment receipts, password resets, security alerts. Always enabled.</div>
            </div>
            <ToggleSwitch checked={true} onChange={() => {}} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>Marketing Emails</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Product updates, tips, and promotional offers.</div>
            </div>
            <ToggleSwitch checked={notifs.product_updates} onChange={v => setNotifs({...notifs, product_updates: v})} />
          </div></div>)}

          {/* Data Deletion */}
          <h4 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Delete Search History</h4>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 10 }}>
            Clear all your previous searches and lead data. Your account and billing remain active.
          </p>
          <button onClick={() => setShowClearHistory(true)} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--warning)',
            background: 'transparent', color: 'var(--warning)',
            cursor: 'pointer', fontSize: 12, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}>
            <Trash2 size={14} /> Clear Search History
          </button>
        </div>

        <div style={{ borderTop: '1px solid var(--error)30', paddingTop: 14, marginTop: 8 }}>
          <h4 style={{ color: 'var(--error)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Closing Your Account</h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 10, lineHeight: 1.6 }}>
            Leaving us? We're sad to see you go. Deleting your account removes everything,  your leads, searches, AI scores, exports, and settings. There's no undo button for this one.
          </p>
          <button onClick={() => setShowDelete(true)} style={{
            padding: '8px 18px', borderRadius: 8, border: '1px solid var(--error)',
            background: 'transparent', color: 'var(--error)',
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6,
          }}><Trash2 size={14} /> Delete My Account</button>
        </div>
      </Section>

      {/* Clear Search History Modal */}
      {showClearHistory && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }} onClick={() => { setShowClearHistory(false); setClearHistoryText(''); }}>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 20, padding: 'var(--space-2xl)', maxWidth: 500, width: '100%', border: '1px solid var(--warning)', boxShadow: 'var(--shadow-xl)' }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <AlertTriangle size={44} color="var(--warning)" style={{ marginBottom: 12 }} />
              <h3 style={{ color: 'var(--warning)', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Clear All Search History?</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                This will <strong style={{ color: 'var(--error)' }}>permanently delete</strong> all your leads, search history, 
                AI scores, pitch angles, and export records. This action <strong>cannot be undone</strong>.
              </p>
              <div style={{ marginTop: 14, padding: '12px 16px', background: 'var(--warning-bg)', borderRadius: 10, border: '1px solid var(--warning)', textAlign: 'left' }}>
                <p style={{ color: 'var(--warning)', fontSize: 11, fontWeight: 600, margin: '0 0 4px' }}>⚠️ What you will lose:</p>
                <ul style={{ color: 'var(--text-secondary)', fontSize: 11, margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
                  <li>All discovered leads and company profiles</li>
                  <li>AI-generated lead scores and pitch angles</li>
                  <li>Pain point analysis and ROI estimates</li>
                  <li>Export history (CSV, Google Sheets)</li>
                  <li>Search history and saved searches</li>
                </ul>
              </div>
              <div style={{ marginTop: 14, padding: '12px 16px', background: 'var(--info-bg)', borderRadius: 10, border: '1px solid var(--info)', textAlign: 'left' }}>
                <p style={{ color: 'var(--info)', fontSize: 11, fontWeight: 600, margin: '0 0 4px' }}>✅ What stays safe:</p>
                <ul style={{ color: 'var(--text-secondary)', fontSize: 11, margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
                  <li>Your account and login credentials</li>
                  <li>Billing history and payment receipts</li>
                  <li>Your plan and subscription status</li>
                  <li>Settings and preferences</li>
                </ul>
              </div>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 11, textAlign: 'center', marginBottom: 12 }}>
              We recommend <strong style={{ color: 'var(--accent-primary)' }}>exporting your data first</strong> using the options above.
            </p>
            <input value={clearHistoryText} onChange={e => setClearHistoryText(e.target.value)} 
              placeholder='Type "DELETE ALL LEADS" to confirm' autoFocus 
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, textAlign: 'center', fontWeight: 600, boxSizing: 'border-box', marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowClearHistory(false); setClearHistoryText(''); }} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>No, keep my data</button>
              <button onClick={handleClearHistory} disabled={clearHistoryText !== 'DELETE ALL LEADS' || clearingHistory} style={{ flex: 1, padding: 12, borderRadius: 10, border: 'none', background: clearHistoryText === 'DELETE ALL LEADS' ? 'var(--error)' : 'var(--bg-tertiary)', color: clearHistoryText === 'DELETE ALL LEADS' ? 'white' : 'var(--text-tertiary)', cursor: clearHistoryText === 'DELETE ALL LEADS' ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700, opacity: clearingHistory ? 0.7 : 1 }}>{clearingHistory ? 'Deleting...' : 'Delete All My Leads'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }} onClick={() => { setShowDelete(false); setDeleteText(''); }}>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 20, padding: 'var(--space-2xl)', maxWidth: 460, width: '100%', border: '1px solid var(--error)', boxShadow: 'var(--shadow-xl)', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <AlertTriangle size={40} color="var(--error)" style={{ marginBottom: 12 }} />
            <h3 style={{ color: 'var(--error)', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Are you sure?</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
              This isn't a temporary pause,  it's goodbye. Every lead you've discovered, every pitch angle we've written, every search you've run,  gone. If there's anything we could've done better, we'd love to hear it before you go.
            </p>
            <input value={deleteText} onChange={e => setDeleteText(e.target.value)} placeholder='Type "goodbye" to confirm' autoFocus style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 14, textAlign: 'center', fontWeight: 600, boxSizing: 'border-box', marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowDelete(false); setDeleteText(''); }} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>I'll stay</button>
              <button onClick={handleDelete} disabled={deleteText !== 'goodbye' || deleting} style={{ flex: 1, padding: 12, borderRadius: 10, border: 'none', background: deleteText === 'goodbye' ? 'var(--error)' : 'var(--bg-tertiary)', color: deleteText === 'goodbye' ? 'white' : 'var(--text-tertiary)', cursor: deleteText === 'goodbye' ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700, opacity: deleting ? 0.7 : 1 }}>{deleting ? 'Deleting...' : 'Goodbye'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Share Button ───
function ShareBtn({ icon, label, color, href }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '8px 14px', borderRadius: 8,
      border: `1px solid ${color}30`, background: `${color}10`,
      color, textDecoration: 'none', fontSize: 12, fontWeight: 600,
      transition: 'all 0.15s ease',
    }}>
      {icon} {label}
    </a>
  );
}

// ─── Integration Card ───
function IntegrationCard({ icon, name, desc }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: 'var(--space-md)', borderRadius: 'var(--radius-md)',
      background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)',
    }}>
      <img src={icon} alt={name} style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0 }} 
        onError={(e) => { e.target.style.display = 'none'; }} />
      <div>
        <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{name}</div>
        <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{desc}</div>
      </div>
    </div>
  );
}

const teamTh = { padding: '10px 12px', textAlign: 'left', color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
const teamTd = { padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 12, whiteSpace: 'nowrap' };
